import { ID } from "@/types/common"

/**
 * 训练计划状态枚举
 */
export enum TrainingPlanStatus {
  /** 未开始 */
  NOT_STARTED = "NOT_STARTED",
  /** 进行中 */
  IN_PROGRESS = "IN_PROGRESS",
  /** 已完成 */
  COMPLETED = "COMPLETED",
  /** 已暂停 */
  SUSPENDED = "SUSPENDED",
}

/**
 * 训练计划实体接口
 * API 返回/页面表格展示的完整训练计划实体
 */
export interface TrainingPlan {
  /** 训练计划ID */
  id: ID
  /** 学生ID */
  studentId: ID
  /** 学生姓名 */
  studentName?: string | null
  /** 训练项目ID */
  trainingProjectId: ID
  /** 训练项目名称 */
  trainingProjectName?: string | null
  /** 训练项目所属二级领域ID */
  trainingProjectDomainLv2Id?: ID | null
  /** 训练项目所属一级领域ID */
  trainingProjectDomainLv1Id?: ID | null
  /** 状态 */
  status: TrainingPlanStatus
  /** 备注信息 */
  note?: string
  /** 计划开始日期 */
  expectedBeginDate?: string
  /** 计划结束日期 */
  expectedEndDate?: string
  /** 实际开始日期 */
  actualBeginDate?: string
  /** 实际结束日期 */
  actualEndDate?: string
}

/**
 * 训练计划表单数据接口
 * 表单提交用的数据结构（创建时使用）
 */
export interface TrainingPlanFormData {
  /** 学生ID */
  studentId: ID
  /** 训练项目ID */
  trainingProjectId: ID
  /** 备注信息 */
  note?: string
  /** 计划开始日期 */
  expectedBeginDate?: string
  /** 计划结束日期 */
  expectedEndDate?: string
  /** 实际开始日期 */
  actualBeginDate?: string
  /** 实际结束日期 */
  actualEndDate?: string
}

/**
 * 训练计划批量创建表单数据接口
 */
export interface TrainingPlanBatchFormData
  extends Omit<TrainingPlanFormData, "trainingProjectId"> {
  /** 训练项目ID列表 */
  trainingProjectIds: ID[]
}

/**
 * 保存训练计划数据（新增或编辑）
 */
export type TrainingPlanSaveData =
  | TrainingPlanFormData
  | TrainingPlanBatchFormData

/**
 * 训练计划查询参数接口
 */
export interface TrainingPlanQueryParams {
  /** 学生ID（可选） */
  studentId?: ID
  /** 状态（可选） */
  status?: TrainingPlanStatus
  /** 训练项目ID（可选） */
  trainingProjectId?: ID
  /** 一级领域ID（可选） */
  domainLv1Id?: ID
  /** 二级领域ID（可选） */
  domainLv2Id?: ID
  /** 页码（可选） */
  page?: number
  /** 每页数量（可选） */
  pageSize?: number
}

/**
 * 训练计划表格组件属性接口
 */
export interface TrainingPlanTableProps {
  /** 训练计划列表数据 */
  trainingPlans: TrainingPlan[]
  /** 编辑训练计划回调函数 */
  onEdit?: (trainingPlan: TrainingPlan) => void
  /** 删除训练计划回调函数 */
  onDelete?: (id: ID) => void
  /** 查看训练记录回调函数 */
  onViewRecords?: (trainingPlan: TrainingPlan) => void
  /** 添加训练记录回调函数 */
  onAddRecord?: (trainingPlan: TrainingPlan) => void
  /** 加载状态 */
  loading?: boolean
  /** 已选中的训练计划ID列表 */
  selectedIds?: ID[]
  /** 选择变化回调函数 */
  onSelectionChange?: (selectedIds: ID[]) => void
}

/**
 * 训练计划模态框组件属性接口
 */
export interface TrainingPlanModalProps {
  /** 模态框是否打开 */
  isOpen: boolean
  /** 关闭回调函数 */
  onClose: () => void
  /** 保存回调函数 */
  onSave: (data: TrainingPlanSaveData) => void
  /** 当前编辑的训练计划信息，为null时表示新增 */
  trainingPlan?: TrainingPlan | null
  /** 提交加载状态 */
  loading?: boolean
  /** 学生列表（从父组件传入） */
  students: Array<{ id: ID; name: string }>
  /** 学生列表加载状态 */
  studentsLoading: boolean
  /** 初始值 */
  initialValues: {
    /** 新增时的初始学生ID */
    studentId: ID | undefined
    /** 新增时的初始一级领域ID */
    domainId?: ID | undefined
    /** 新增时的初始二级领域ID */
    domainLv2Id?: ID | undefined
  }
}
